// MonsterData.ts - 怪物数据库
// 包含大量怪物、Boss、精英怪数据

export interface MonsterData {
  id: string;
  name: string;
  description: string;
  type: 'normal' | 'elite' | 'boss' | 'world_boss';
  level: number;
  hp: number;
  mp: number;
  attack: number;
  defense: number;
  speed: number;
  skills: string[];
  drops: { itemId: string; chance: number; min: number; max: number }[];
  exp: number;
  gold: number;
  spawnLocations: string[];
  spawnRate: number;
  isPassive: boolean;
  weaknesses: string[];
  resistances: string[];
  sprite: string;
}

export const MONSTERS: MonsterData[] = [];

const monsterTypes = [
  { name: '野狼', type: 'normal', baseHp: 50, baseAtk: 10, baseDef: 5 },
  { name: '野猪', type: 'normal', baseHp: 80, baseAtk: 15, baseDef: 8 },
  { name: '棕熊', type: 'elite', baseHp: 200, baseAtk: 30, baseDef: 20 },
  { name: '毒蛇', type: 'normal', baseHp: 40, baseAtk: 20, baseDef: 3 },
  { name: '山贼', type: 'normal', baseHp: 100, baseAtk: 25, baseDef: 10 },
  { name: '土匪头目', type: 'elite', baseHp: 300, baseAtk: 40, baseDef: 25 },
  { name: '巨型蜘蛛', type: 'elite', baseHp: 150, baseAtk: 35, baseDef: 15 },
  { name: '食人花', type: 'normal', baseHp: 60, baseAtk: 18, baseDef: 12 },
  { name: '岩石怪', type: 'elite', baseHp: 250, baseAtk: 25, baseDef: 35 },
  { name: '幽灵', type: 'normal', baseHp: 70, baseAtk: 30, baseDef: 5 },
  { name: '僵尸', type: 'normal', baseHp: 120, baseAtk: 20, baseDef: 18 },
  { name: '骷髅战士', type: 'normal', baseHp: 90, baseAtk: 28, baseDef: 12 },
  { name: '骷髅弓箭手', type: 'normal', baseHp: 60, baseAtk: 35, baseDef: 8 },
  { name: '黑暗骑士', type: 'elite', baseHp: 400, baseAtk: 50, baseDef: 40 },
  { name: '炎魔', type: 'boss', baseHp: 1000, baseAtk: 80, baseDef: 50 },
  { name: '冰龙', type: 'boss', baseHp: 1200, baseAtk: 70, baseDef: 60 },
  { name: '雷电鸟', type: 'elite', baseHp: 350, baseAtk: 55, baseDef: 25 },
  { name: '史莱姆', type: 'normal', baseHp: 30, baseAtk: 5, baseDef: 2 },
  { name: '史莱姆王', type: 'elite', baseHp: 500, baseAtk: 40, baseDef: 30 },
  { name: '哥布林', type: 'normal', baseHp: 45, baseAtk: 12, baseDef: 4 },
];

// 生成1000个怪物
for (let i = 1; i <= 1000; i++) {
  const template = monsterTypes[i % monsterTypes.length];
  const level = Math.floor(i / 10) + 1;
  const isElite = i % 10 === 0;
  const isBoss = i % 100 === 0;
  
  const type = isBoss ? 'boss' : isElite ? 'elite' : template.type;
  const hpMultiplier = isBoss ? 10 : isElite ? 3 : 1;
  const atkMultiplier = isBoss ? 5 : isElite ? 2 : 1;
  
  MONSTERS.push({
    id: `monster_${String(i).padStart(4, '0')}`,
    name: `${template.name}${i > 20 ? `Lv${level}` : ''}`,
    description: `等级${level}的${template.name}，${isBoss ? '强大的Boss' : isElite ? '精英怪物' : '普通怪物'}`,
    type: type as any,
    level: level,
    hp: Math.floor(template.baseHp * level * hpMultiplier),
    mp: Math.floor(template.baseHp * 0.3),
    attack: Math.floor(template.baseAtk * level * atkMultiplier),
    defense: Math.floor(template.baseDef * level),
    speed: 10 + level,
    skills: [`skill_${(i % 50) + 1}`],
    drops: [
      { itemId: `item_${(i % 1000) + 1}`, chance: 0.3, min: 1, max: 3 },
      { itemId: `gold`, chance: 1.0, min: level * 10, max: level * 20 }
    ],
    exp: level * (isBoss ? 100 : isElite ? 30 : 10),
    gold: level * (isBoss ? 50 : isElite ? 20 : 5),
    spawnLocations: [`loc_${(i % 200) + 1}`],
    spawnRate: isBoss ? 0.01 : isElite ? 0.05 : 0.2,
    isPassive: i % 3 === 0,
    weaknesses: i % 5 === 0 ? ['fire'] : i % 7 === 0 ? ['ice'] : [],
    resistances: i % 4 === 0 ? ['physical'] : [],
    sprite: `spr_${template.name}`
  });
}

// ========== 装备数据库 ==========
export interface EquipmentData {
  id: string;
  name: string;
  description: string;
  slot: 'weapon' | 'helmet' | 'armor' | 'gloves' | 'boots' | 'ring' | 'necklace' | 'cape';
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary' | 'mythic';
  level: number;
  stats: { [key: string]: number };
  setId?: string;
  effects?: string[];
  icon: string;
}

export const EQUIPMENT: EquipmentData[] = [];

const equipmentSlots: EquipmentData['slot'][] = ['weapon', 'helmet', 'armor', 'gloves', 'boots', 'ring', 'necklace', 'cape'];
const equipmentPrefixes = ['破旧的', '普通的', '精致的', '稀有的', '传说的', '神话的'];
const equipmentNames: Record<string, string[]> = {
  weapon: ['剑', '刀', '枪', '斧', '锤', '弓', '法杖', '匕首', '长矛', '拳套'],
  helmet: ['头盔', '帽子', '头巾', '王冠', '面具', '兜帽'],
  armor: ['铠甲', '护甲', '长袍', '皮甲', '锁甲', '板甲'],
  gloves: ['手套', '护手', '护腕', '指虎', '护臂'],
  boots: ['靴子', '鞋子', '护腿', '战靴', '凉鞋'],
  ring: ['戒指', '指环', '戒饰', '魔戒'],
  necklace: ['项链', '吊坠', '护身符', '念珠'],
  cape: ['披风', '斗篷', ' mantle', '围巾']
};

// 生成2000件装备
for (let i = 1; i <= 2000; i++) {
  const slot = equipmentSlots[i % equipmentSlots.length];
  const rarityIndex = Math.floor(i / 400);
  const rarity = ['common', 'uncommon', 'rare', 'epic', 'legendary', 'mythic'][rarityIndex] as EquipmentData['rarity'];
  const name = equipmentNames[slot][i % equipmentNames[slot].length];
  const level = Math.floor(i / 20) + 1;
  
  EQUIPMENT.push({
    id: `equip_${String(i).padStart(4, '0')}`,
    name: `${equipmentPrefixes[rarityIndex]}${name}`,
    description: `${rarity}品质的${slot}装备，适合${level}级使用`,
    slot: slot,
    rarity: rarity,
    level: level,
    stats: {
      attack: slot === 'weapon' ? level * (rarityIndex + 1) * 2 : 0,
      defense: slot === 'armor' || slot === 'helmet' ? level * (rarityIndex + 1) : 0,
      hp: level * 10 * (rarityIndex + 1),
      mp: level * 5 * (rarityIndex + 1)
    },
    setId: i % 100 < 20 ? `set_${Math.floor(i / 100)}` : undefined,
    effects: rarityIndex >= 3 ? [`effect_${i % 20}`] : undefined,
    icon: `${slot}_${i}`
  });
}

// ========== 副本数据库 ==========
export interface DungeonData {
  id: string;
  name: string;
  description: string;
  minLevel: number;
  maxLevel: number;
  floors: number;
  monsters: string[];
  boss: string;
  rewards: { exp: number; gold: number; items: string[] };
  dailyAttempts: number;
  icon: string;
}

export const DUNGEONS: DungeonData[] = [];

const dungeonTypes = [
  { name: '森林迷宫', prefix: '幽暗' },
  { name: '地下城', prefix: '深渊' },
  { name: '遗迹', prefix: '古代' },
  { name: '洞穴', prefix: '恐怖' },
  { name: '高塔', prefix: '通天' },
  { name: '神殿', prefix: '神圣' },
  { name: '古墓', prefix: '千年' },
  { name: '火山', prefix: '烈焰' },
  { name: '冰原', prefix: '永恒' },
  { name: '沙漠', prefix: '无尽' }
];

// 生成100个副本
for (let i = 1; i <= 100; i++) {
  const dungeon = dungeonTypes[i % dungeonTypes.length];
  const minLevel = Math.floor(i / 5) * 10 + 1;
  const floors = 5 + (i % 20);
  
  DUNGEONS.push({
    id: `dungeon_${String(i).padStart(3, '0')}`,
    name: `${dungeon.prefix}${dungeon.name}${i}`,
    description: `等级${minLevel}-${minLevel + 9}的${dungeon.name}副本，共${floors}层`,
    minLevel: minLevel,
    maxLevel: minLevel + 9,
    floors: floors,
    monsters: Array.from({ length: 10 }, (_, j) => `monster_${String((i * 10 + j) % 1000 + 1).padStart(4, '0')}`),
    boss: `monster_${String((i * 100) % 1000 + 1).padStart(4, '0')}`,
    rewards: {
      exp: minLevel * 1000,
      gold: minLevel * 500,
      items: [`equip_${(i % 2000) + 1}`, `item_${(i % 1000) + 1}`]
    },
    dailyAttempts: 3,
    icon: `dungeon_${dungeon.name}`
  });
}

// ========== 技能数据库 ==========
export interface SkillExtended {
  id: string;
  name: string;
  description: string;
  type: 'active' | 'passive' | 'buff' | 'debuff';
  cooldown: number;
  mpCost: number;
  hpCost: number;
  target: 'self' | 'enemy' | 'ally' | 'area' | 'all_enemies' | 'all_allies';
  damage?: { type: string; value: number };
  heal?: number;
  buff?: { stat: string; value: number; duration: number };
  debuff?: { stat: string; value: number; duration: number };
  icon: string;
}

export const SKILLS_EXTENDED: SkillExtended[] = [];

const skillTypes = ['攻击', '防御', '治疗', '辅助', '控制', '召唤'];
const elements = ['火', '冰', '雷', '风', '土', '水', '光', '暗'];

// 生成500个技能
for (let i = 1; i <= 500; i++) {
  const type = skillTypes[i % skillTypes.length];
  const element = elements[i % elements.length];
  
  SKILLS_EXTENDED.push({
    id: `skill_ext_${String(i).padStart(4, '0')}`,
    name: `${element}${type}${i}`,
    description: `使用${element}之力进行${type}的技能`,
    type: i % 4 === 0 ? 'buff' : i % 4 === 1 ? 'debuff' : i % 4 === 2 ? 'passive' : 'active',
    cooldown: (i % 10 + 1) * 10,
    mpCost: i * 5,
    hpCost: i % 10 === 0 ? i * 2 : 0,
    target: ['self', 'enemy', 'ally', 'area', 'all_enemies', 'all_allies'][i % 6] as any,
    damage: i % 3 === 0 ? { type: element, value: i * 10 } : undefined,
    heal: i % 5 === 0 ? i * 8 : undefined,
    buff: i % 4 === 0 ? { stat: 'attack', value: i * 2, duration: 30 } : undefined,
    debuff: i % 4 === 1 ? { stat: 'defense', value: -i, duration: 20 } : undefined,
    icon: `skill_${element}_${type}`
  });
}

// ========== 宠物数据库 ==========
export interface PetData {
  id: string;
  name: string;
  description: string;
  type: 'combat' | 'support' | 'gather' | 'mount';
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
  level: number;
  maxLevel: number;
  stats: { hp: number; attack: number; defense: number; speed: number };
  skills: string[];
  growthRate: number;
  feedType: string;
  icon: string;
}

export const PETS: PetData[] = [];

const petTypes = ['战斗宠', '辅助宠', '采集宠', '坐骑'];
const petNames = ['小狗', '小猫', '小狼', '小熊', '小鸟', '小龙', '小精灵', '小恶魔', '小天使', '史莱姆'];

// 生成300个宠物
for (let i = 1; i <= 300; i++) {
  const rarity = ['common', 'uncommon', 'rare', 'epic', 'legendary'][Math.floor(i / 60)] as PetData['rarity'];
  
  PETS.push({
    id: `pet_${String(i).padStart(4, '0')}`,
    name: `${petNames[i % petNames.length]}${i}`,
    description: `可爱的${petNames[i % petNames.length]}，可以协助战斗`,
    type: ['combat', 'support', 'gather', 'mount'][i % 4] as PetData['type'],
    rarity: rarity,
    level: 1,
    maxLevel: 50 + Math.floor(i / 10) * 10,
    stats: {
      hp: 100 + i * 10,
      attack: 20 + i * 2,
      defense: 10 + i,
      speed: 15 + Math.floor(i / 2)
    },
    skills: [`skill_${(i % 50) + 1}`],
    growthRate: 1 + (i % 10) * 0.1,
    feedType: ['meat', 'fish', 'vegetable', 'magic'][i % 4],
    icon: `pet_${i}`
  });
}

// 导出所有数据
export default {
  MONSTERS,
  EQUIPMENT,
  DUNGEONS,
  SKILLS_EXTENDED,
  PETS
};
